/**
 * 「绑定邮箱订阅」Prompt Sheet 的展示 gate：
 * 是否弹出的判断全部收敛在此（调用方只管调用 opener）。
 *
 * 三层判断：
 * 1. 会话抑制 —— 用户点过 Not now / 已成功订阅 → 本会话不再弹
 *    （sessionStorage + 内存双写，SSR 安全）；
 * 2. 服务端订阅状态 —— resolver 查询用户是否已订阅，
 *    已订阅（"subscribed"）→ 不弹；"unknown"（接口失败/未注册）→ 仍弹出（fail-open）；
 * 3. resolver 由接入方在应用启动时通过 `setSubscribeEmailStatusResolver` 注册一次，
 *    后端订阅状态接口落地前默认返回 "unknown"。
 */

export type SubscribeEmailStatus = "subscribed" | "not_subscribed" | "unknown";

/** 订阅状态查询；后端接口落地后实现并在启动时注册 */
export type SubscribeEmailStatusResolver = () => Promise<SubscribeEmailStatus>;

let statusResolver: SubscribeEmailStatusResolver = async () => "unknown";

/**
 * 注册订阅状态查询（应用启动时调用一次）。
 * 示例：
 *   setSubscribeEmailStatusResolver(async () => {
 *     const { data } = await api.xxx.getSubscribeStatus();
 *     return data?.subscribed ? "subscribed" : "not_subscribed";
 *   });
 */
export function setSubscribeEmailStatusResolver(
  resolver: SubscribeEmailStatusResolver,
): void {
  statusResolver = resolver;
}

/* ------------------------- 会话级抑制 ------------------------- */

const SUPPRESS_KEY = "subscribe_email_prompt_suppressed";
let suppressedInMemory = false;

/** SSR 安全地读会话抑制标记 */
function readSuppressed(): boolean {
  if (suppressedInMemory) {
    return true;
  }
  try {
    suppressedInMemory =
      globalThis.sessionStorage?.getItem(SUPPRESS_KEY) === "1";
  } catch {
    // sessionStorage 不可用（隐私模式等）→ 仅依赖内存标记
  }
  return suppressedInMemory;
}

/** 用户主动跳过或已订阅成功 → 本会话不再弹 */
export function suppressSubscribeEmailPrompt(): void {
  suppressedInMemory = true;
  try {
    globalThis.sessionStorage?.setItem(SUPPRESS_KEY, "1");
  } catch {
    // ignore
  }
}

/** 清除抑制（调试 / 用户重新进入订阅流程时用） */
export function resetSubscribeEmailPromptSuppression(): void {
  suppressedInMemory = false;
  try {
    globalThis.sessionStorage?.removeItem(SUPPRESS_KEY);
  } catch {
    // ignore
  }
}

export function isSubscribeEmailPromptSuppressed(): boolean {
  return readSuppressed();
}

/**
 * 汇总判断：是否应该弹出。
 * - 会话抑制中 → { show: false, reason: "suppressed" }
 * - 服务端已订阅 → { show: false, reason: "subscribed" }
 * - 未订阅 / unknown / 查询异常 → { show: true }
 */
export async function shouldShowSubscribeEmailPrompt(): Promise<
  { show: true; reason?: undefined } | { show: false; reason: string }
> {
  if (isSubscribeEmailPromptSuppressed()) {
    return { show: false, reason: "suppressed" };
  }
  try {
    const status = await statusResolver();
    if (status === "subscribed") {
      return { show: false, reason: "subscribed" };
    }
  } catch {
    // 查询失败按 unknown 处理，不阻塞弹出
  }
  return { show: true };
}
